/**
 * Game Constants - Centralized configuration for the game
 */

// =============================================================================
// Lane Configuration
// =============================================================================

/** Width of each lane in world units */
export const LANE_WIDTH = 2.5;

/** Number of lanes (0 = left, 1 = center, 2 = right) */
export const LANE_COUNT = 3;

/** Lane positions from center */
export const LANE_POSITIONS = [-LANE_WIDTH, 0, LANE_WIDTH];

// =============================================================================
// Player Physics
// =============================================================================

export const PLAYER_PHYSICS = {
  GRAVITY: -35,
  JUMP_FORCE: 12,
  GROUND_Y: 0.6,
  LANE_SWITCH_SPEED: 10,
} as const;

// =============================================================================
// Camera Configuration
// =============================================================================

export const CAMERA_CONFIG = {
  PORTRAIT: {
    position: { x: 0, y: 8, z: -10 },
    lookAt: { x: 0, y: 0, z: 20 },
    fov: 75,
  },
  TABLET: {
    position: { x: 3, y: 6, z: -6 },
    lookAt: { x: 0, y: 0.5, z: 20 },
    fov: 72,
  },
  DESKTOP: {
    position: { x: 5, y: 3.5, z: -3 },
    lookAt: { x: 0, y: 1, z: 25 },
    fov: 70,
  },
} as const;

// =============================================================================
// World Configuration
// =============================================================================

export const WORLD_CONFIG = {
  TILE_LENGTH: 20,
  TILE_COUNT: 5,
  GROUND_WIDTH: 15,
  INITIAL_OBSTACLE_Z: 50,
} as const;

// =============================================================================
// Obstacle Configuration
// =============================================================================

export const OBSTACLE_COLORS = {
  block: 0xe74c3c,   // Red
  spike: 0xf39c12,   // Orange
  wall: 0x9b59b6,    // Purple
  low: 0x3498db,     // Blue
} as const;

export const OBSTACLE_DIMENSIONS = {
  block: { width: 1.2, height: 1.2, depth: 1.2, baseY: 0.6 },
  spike: { radius: 0.5, height: 1.5, segments: 4, baseY: 0.75 },
  wall: { width: 2.2, height: 2.5, depth: 0.4, baseY: 1.25 },
  low: { width: 1.5, height: 0.6, depth: 1.5, baseY: 0.3 },
} as const;

// =============================================================================
// Input Configuration
// =============================================================================

export const INPUT_CONFIG = {
  SWIPE_THRESHOLD: 30,
  TAP_ZONE_LEFT: 0.3,   // 30% from left
  TAP_ZONE_RIGHT: 0.7,  // 70% from left
} as const;

// =============================================================================
// Audio Configuration
// =============================================================================

export const AUDIO_CONFIG = {
  BGM_VOLUME: 0.3,
  SFX_VOLUME: 0.5,
  BGM_TEMPO_MS: 350,
} as const;

// =============================================================================
// Types
// =============================================================================

/** Axis-Aligned Bounding Box for collision detection */
export interface AABB {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
}

/** Game difficulty levels */
export type Difficulty = 'easy' | 'normal' | 'hard' | 'insane';

/** Game states */
export type GameState = 'start' | 'playing' | 'gameover';

/** Player input actions */
export type InputAction = 'jump' | 'left' | 'right';

/** Difficulty settings interface */
export interface DifficultySettings {
  baseSpeed: number;
  maxSpeed: number;
  speedIncreaseRate: number;
  minObstacleGap: number;
  maxObstacleGap: number;
  label: string;
  shareEmoji: string;
  shareText: string;
}

/** Difficulty presets */
export const DIFFICULTY_SETTINGS: Record<Difficulty, DifficultySettings> = {
  easy: {
    baseSpeed: 12,
    maxSpeed: 30,
    speedIncreaseRate: 0.15,
    minObstacleGap: 18,
    maxObstacleGap: 30,
    label: 'イージー',
    shareEmoji: '🤖🏃',
    shareText: '走りました',
  },
  normal: {
    baseSpeed: 15,
    maxSpeed: 40,
    speedIncreaseRate: 0.30,
    minObstacleGap: 12,
    maxObstacleGap: 22,
    label: 'ノーマル',
    shareEmoji: '🤖🏃',
    shareText: '走りました',
  },
  hard: {
    baseSpeed: 18,
    maxSpeed: 50,
    speedIncreaseRate: 0.45,
    minObstacleGap: 8,
    maxObstacleGap: 15,
    label: 'ハード',
    shareEmoji: '🔥🤖',
    shareText: '走りました',
  },
  insane: {
    baseSpeed: 22,
    maxSpeed: 60,
    speedIncreaseRate: 0.60,
    minObstacleGap: 5,
    maxObstacleGap: 10,
    label: '鬼畜',
    shareEmoji: '💀🤖',
    shareText: '生き延びました',
  },
};

